import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { logger, prepareSearchPhoneNumber } from 'shared/lib'
import { TableStore } from 'shared/ui/TableNew/store/tableStore'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { toastStore } from 'shared/ui'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { RowInbox } from 'entities/Inbox/model/Table/RowInbox'
import { type IResponseInbox } from 'entities/Inbox/api/types'
import { InboxesApi } from 'entities/Inbox'
import { type InboxesStore } from 'pages/settings/pages/inboxes/store/inboxesStore'

export class InboxesViewStore {
  private readonly _tableStore: TableStore<RowInbox>
  private _itemsMap: Map<number, Inbox> = new Map()
  private _firstLoading = true
  private _search = ''

  constructor(private _inboxesStore: InboxesStore, private _entities: IEntitiesStore) {
    this._tableStore = new TableStore({
      key: 'inboxesTable',
      element: 'inboxes',
      sortOrder: 'asc',
      sortBy: 'name',
      useColumnsCache: true,
    })
    makeAutoObservable(this)
  }

  init = () => {
    this.loadData()
  }

  get items() {
    return Array.from(this._itemsMap.values()).filter((item) => this.hasInboxAccess(item))
  }

  get inboxesStore() {
    return this._inboxesStore
  }

  getItem = (id: number) => {
    return this.items.find((item) => item.id === id)
  }

  get itemsRows() {
    return this.items.map((item) => new RowInbox(item, this._entities))
  }

  get filteredRows() {
    let filtered = this.itemsRows

    if (this._search.trim()) {
      const searchTerm = this._search.toLowerCase().trim()
      const numberSearchTerm = prepareSearchPhoneNumber(searchTerm)

      filtered = filtered.filter((row) => {
        const nameMatch = row.origin.name.toLowerCase().includes(searchTerm)
        const phoneMatch =
          row.numbersCount === 1 && row.firstNumber?.number.includes(numberSearchTerm)

        return nameMatch || phoneMatch
      })
    }

    return filtered
  }

  get sortedRows() {
    const { sortBy, sortOrder } = this._tableStore
    const filtered = this.filteredRows

    if (!sortBy) return filtered

    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.origin.name.localeCompare(b.origin.name)
        case 'owner':
          return (a.owner?.full_name || '').localeCompare(b.owner?.full_name || '')
        case 'members':
          return a.members.length - b.members.length
        case 'created_at':
          return new Date(a.createdAt || '').getTime() - new Date(b.createdAt || '').getTime()
        default:
          return 0
      }
    })

    return sortOrder === 'asc' ? sorted : sorted.reverse()
  }

  get tableStore() {
    return this._tableStore
  }

  get firstLoading() {
    return this._firstLoading
  }

  get loading() {
    return this._tableStore.loading
  }

  get entities() {
    return this._entities
  }

  get search() {
    return this._search
  }

  hasInboxAccess = (inbox: Inbox): boolean => {
    const currentUser = this._inboxesStore.currentUser

    if (this._inboxesStore.shouldRestrictListAccess()) {
      return inbox.memberIds.includes(currentUser.id) || inbox.ownerId === currentUser.id
    }

    return true
  }

  setSearch = (value: string) => {
    this._search = value
  }

  loadData = async () => {
    this._firstLoading = true
    try {
      runInAction(() => {
        this._tableStore.setLoading(true)
      })

      const { data } = await this._entities.inboxesStore.getTeamsAll()

      runInAction(() => {
        this._itemsMap.clear()
        data.forEach((item) => {
          this.setItem(item)
        })
      })
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._tableStore.setLoading(false)
        this._firstLoading = false
      })
    }
  }

  setItem = (data: IResponseInbox) => {
    this._itemsMap.set(data.id, new Inbox(data, this._entities))
  }

  showAccessModal = () => {
    const id = nanoid()
    modalStore.addModal({
      id: id,
      type: ModalTypeList.INFO,
      width: 280,
      title: 'Only Inbox Owners can manage inboxes',
      desc: 'Only the assigned owner can edit or delete an inbox',
      onClose: () => modalStore.removeModal(id),
      infoModalTextAction: 'Cancel',
      closeActionButtonProps: {
        contained: 'secondary',
      },
    })
  }

  makeDefaultInbox = async (id: number) => {
    try {
      const { data } = await InboxesApi.makeDefaultInbox(id)
      this._entities.usersStore.user?.setDefaultTeamId(data.default_team_id)

      toastStore.add({
        title: 'Primary inbox updated successfully.',
        type: 'success',
      })
    } catch (error) {
      toastStore.add({
        title: 'We couldn’t set this inbox as primary. Please try again later.',
        type: 'error',
      })
      logger.error(error)
    }
  }
}
